import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StorefrontAPI from 'utilities/storefrontApi';
import { loadSession } from 'utilities/storefrontSession';
import * as Cart from 'utilities/storefrontCart';
import { clearBoth } from 'utilities/storefrontCartSync';
import { SHELL, MAX, CARD, SUB_INPUT, BUTTON, brandPath, formatMoney } from './styles';
import useBrand from './useBrand';
import StorefrontNav from './StorefrontNav';
import { ShellMessage, Row, Field } from './shellHelpers';

/**
 * Checkout: shipping form + coupon + place-order.
 *
 * Pre-fills shipping from the signed-in customer's profile (display
 * name, contact email/phone, first address). Requires sign-in before
 * we can place an order (so it binds to a customer record + the
 * "your orders" page picks it up).
 *
 * Stripe-hosted payment is the happy path: on success Stripe redirects
 * back to `/order/:id?session=…`; on cancel to `/cart?cancelled=1`.
 */
const emptyShip = () => ({
  name: '', email: '', phone: '',
  line1: '', line2: '', city: '', region: '', postal_code: '', country: '',
});

export default function StorefrontCheckout() {
  const { brand, error } = useBrand();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [ship, setShip] = useState(emptyShip());
  const [signedIn, setSignedIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');
  const [couponCode, setCouponCode] = useState('');
  const [coupon, setCoupon] = useState(null);
  const [couponBusy, setCouponBusy] = useState(false);
  const [couponErr, setCouponErr] = useState('');
  const accent = brand?.brand_color || '#422AFB';

  useEffect(() => {
    if (!brand?._id) return;
    setItems(Cart.loadCart(brand._id));
    setSignedIn(Boolean(loadSession(brand._id)?.token));
    if (!loadSession(brand._id)?.token) return;
    StorefrontAPI.me(brand._id).then((me) => {
      const a = (me?.addresses || [])[0] || {};
      setShip((s) => ({
        ...s,
        name: me?.display_name || s.name,
        email: me?.contact_email || s.email,
        phone: me?.contact_phone || s.phone,
        line1: a.line1 || s.line1,
        line2: a.line2 || s.line2,
        city: a.city || s.city,
        region: a.region || s.region,
        postal_code: a.postal_code || s.postal_code,
        country: a.country || s.country,
      }));
    }).catch(() => { /* anonymous shopper */ });
  }, [brand?._id]);

  const totals = useMemo(() => Cart.cartTotals(items), [items]);
  const setField = (k) => (e) => setShip({ ...ship, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!brand) return;
    setErr('');
    if (!signedIn) {
      navigate(brandPath(brand, '/login'));
      return;
    }
    if (items.length === 0) { setErr('Your cart is empty.'); return; }
    setSubmitting(true);
    try {
      const return_base = window.location.origin;
      const return_path_base = brand?._id && window.location.pathname.startsWith(`/site/${brand._id}`)
        ? `/site/${brand._id}`
        : '';
      const result = await StorefrontAPI.checkout(brand._id, {
        items: items.map((it) => ({ variant_id: it.variant_id, quantity: it.quantity })),
        shipping_address: {
          name: ship.name, email: ship.email, phone: ship.phone,
          line1: ship.line1, line2: ship.line2,
          city: ship.city, region: ship.region,
          postal_code: ship.postal_code, country: ship.country,
        },
        currency: totals.currency,
        discount_code: coupon?.code || undefined,
        return_base,
        return_path_base,
      });
      // Both copies: a basket that survives its own order would be waiting
      // for them, full, on the next device they open.
      clearBoth(brand._id);
      if (result?.payment?.session_url) {
        window.location.href = result.payment.session_url;
        return;
      }
      navigate(brandPath(brand, `/order/${result.order._id}`));
    } catch (e2) {
      setErr(e2?.message || 'Could not place order');
    } finally {
      setSubmitting(false);
    }
  };

  const applyCoupon = async () => {
    setCouponErr(''); setCouponBusy(true);
    try {
      const res = await StorefrontAPI.previewDiscount(brand._id, {
        code: couponCode.trim(),
        subtotal: totals.subtotal,
        currency: totals.currency,
      });
      if (res?.ok) setCoupon({ code: res.code, amount: res.amount });
      else setCouponErr(res?.reason || 'Code not valid');
    } catch (e2) {
      setCouponErr(e2?.message || 'Could not check code');
    } finally {
      setCouponBusy(false);
    }
  };

  if (error) return <ShellMessage>{error}</ShellMessage>;
  if (!brand) return <ShellMessage>Loading…</ShellMessage>;
  if (items.length === 0) {
    return (
      <div style={SHELL}>
        <StorefrontNav brand={brand} />
        <div style={MAX}>
          <h1 style={{ fontSize: 28, margin: '8px 0 24px' }}>Checkout</h1>
          <div style={CARD}>
            <div style={{ color: '#64748b', marginBottom: 14 }}>Your cart is empty.</div>
            <button onClick={() => navigate(brandPath(brand, '/shop'))} style={BUTTON(accent)}>
              Continue shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={SHELL}>
      <StorefrontNav brand={brand} />
      <div style={MAX}>
        <h1 style={{ fontSize: 28, margin: '8px 0 24px' }}>Checkout</h1>
        <form onSubmit={submit} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)', gap: 24 }}>
          <div style={CARD}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#475569', marginBottom: 10 }}>Shipping</div>
            {!signedIn && (
              <div style={{
                background: '#fef3c7', color: '#78350f',
                padding: 10, borderRadius: 8, fontSize: 13, marginBottom: 12,
              }}>
                You need to{' '}
                <button
                  type='button'
                  onClick={() => navigate(brandPath(brand, '/login'))}
                  style={{ color: accent, fontWeight: 600, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline' }}
                >
                  sign in
                </button>{' '}before placing an order.
              </div>
            )}
            <Field label='Full name'><input style={SUB_INPUT} value={ship.name} onChange={setField('name')} required /></Field>
            <Field label='Email'><input style={SUB_INPUT} type='email' value={ship.email} onChange={setField('email')} required /></Field>
            <Field label='Phone'><input style={SUB_INPUT} value={ship.phone} onChange={setField('phone')} /></Field>
            <Field label='Address line 1'><input style={SUB_INPUT} value={ship.line1} onChange={setField('line1')} required /></Field>
            <Field label='Address line 2'><input style={SUB_INPUT} value={ship.line2} onChange={setField('line2')} /></Field>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10 }}>
              <Field label='City'><input style={SUB_INPUT} value={ship.city} onChange={setField('city')} required /></Field>
              <Field label='Region'><input style={SUB_INPUT} value={ship.region} onChange={(e) => setShip({ ...ship, region: e.target.value.toUpperCase() })} placeholder='ON' /></Field>
              <Field label='Postal'><input style={SUB_INPUT} value={ship.postal_code} onChange={setField('postal_code')} /></Field>
            </div>
            <Field label='Country (ISO-2)'>
              <input
                style={SUB_INPUT}
                value={ship.country}
                onChange={(e) => setShip({ ...ship, country: e.target.value.toUpperCase().slice(0, 2) })}
                placeholder='CA' required maxLength={2}
              />
            </Field>
            {err && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 10 }}>{err}</div>}
          </div>

          <div style={CARD}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#475569', marginBottom: 10 }}>Order summary</div>
            {items.map((it) => (
              <div key={it.variant_id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: 13 }}>
                <div style={{ paddingRight: 8 }}>
                  <div style={{ fontWeight: 600 }}>{it.name}</div>
                  {it.variant_name && <div style={{ color: '#64748b', fontSize: 12 }}>{it.variant_name}</div>}
                  <div style={{ color: '#64748b', fontSize: 12 }}>× {it.quantity}</div>
                </div>
                <div style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>
                  {formatMoney(Number(it.unit_price) * Number(it.quantity), it.currency)}
                </div>
              </div>
            ))}
            <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 10, paddingTop: 10 }}>
              <Row label='Subtotal'>{formatMoney(totals.subtotal, totals.currency)}</Row>
              {coupon && (
                <Row label={`Discount (${coupon.code})`}>
                  -{formatMoney(coupon.amount, totals.currency)}
                </Row>
              )}
              <Row label='Tax + shipping' muted>computed on review</Row>
            </div>
            <div style={{ marginTop: 10, borderTop: '1px solid #e5e7eb', paddingTop: 10 }}>
              <div style={{ fontWeight: 700, fontSize: 12, color: '#475569', marginBottom: 6, textTransform: 'uppercase' }}>
                Coupon code
              </div>
              {coupon ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>{coupon.code} applied</div>
                  <button
                    type='button'
                    onClick={() => { setCoupon(null); setCouponCode(''); setCouponErr(''); }}
                    style={{ background: 'transparent', border: 'none', color: '#dc2626', fontSize: 12, cursor: 'pointer' }}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    style={{ ...SUB_INPUT, flex: 1, textTransform: 'uppercase' }}
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder='SAVE10'
                  />
                  <button
                    type='button'
                    disabled={!couponCode.trim() || couponBusy}
                    onClick={applyCoupon}
                    style={{
                      padding: '0 14px', borderRadius: 8, border: 'none',
                      background: accent, color: 'white', fontWeight: 600, fontSize: 13,
                      cursor: 'pointer', opacity: (!couponCode.trim() || couponBusy) ? 0.6 : 1,
                    }}
                  >
                    {couponBusy ? '…' : 'Apply'}
                  </button>
                </div>
              )}
              {couponErr && (
                <div style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>{couponErr}</div>
              )}
            </div>
            <button type='submit' style={{ ...BUTTON(accent), width: '100%', marginTop: 14 }} disabled={submitting}>
              {submitting ? 'Placing…' : 'Place order'}
            </button>
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 8 }}>
              Tax + shipping are calculated server-side from the resolved warehouse and brand tax rules.
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
